import {
    linkModelsInVendor,
    unLinkModelsInVendor,
    autoCreateVendor,
    autoSetupFeedback,
    migrateGroupsToVendor,
    syncSigningKeys,
} from '@/vuex/modules/feedback-linking/feedback-linking.utils';

export default {
    namespaced: true,
    state: {
        linking: false,
        linkingError: {},
        linkingResult: {},
        unLinking: false,
        unLinkingError: {},
        unLinkingResult: {},
        creatingVendor: false,
        creatingVendorError: null,
        migratingGroups: false,
        migratingGroupsError: null,
        migratingGroupsResult: {},
        autoSettingUpFeedback: false,
        autoSettingUpFeedbackError: null,
        syncingSigningKeys: false,
        syncingSigningKeysError: null,
    },
    mutations: {
        setLinking (state, { linking }) {
            state.linking = linking;
        },
        setLinkingError (state, { linkingError }) {
            state.linkingError = linkingError;
        },
        setLinkingResult (state, { linkingResult }) {
            state.linkingResult = linkingResult;
        },
        setUnLinking (state, { unLinking }) {
            state.unLinking = unLinking;
        },
        setUnLinkingError (state, { unLinkingError }) {
            state.unLinkingError = unLinkingError;
        },
        setUnLinkingResult (state, { unLinkingResult }) {
            state.unLinkingResult = unLinkingResult;
        },
        setMigratingGroups (state, { migratingGroups }) {
            state.migratingGroups = migratingGroups;
        },
        setMigratingGroupsError (state, { migratingGroupsError }) {
            state.migratingGroupsError = migratingGroupsError;
        },
        setMigratingGroupsResult (state, { migratingGroupsResult }) {
            state.migratingGroupsResult = migratingGroupsResult;
        },
        setCreatingVendor (state, { creatingVendor }) {
            state.creatingVendor = creatingVendor;
        },
        setCreatingVendorError (state, { creatingVendorError }) {
            state.creatingVendorError = creatingVendorError;
        },
        setAutoSettingUpFeedback (state, { autoSettingUpFeedback }) {
            state.autoSettingUpFeedback = autoSettingUpFeedback;
        },
        setAutoSettingUpFeedbackError (state, { autoSettingUpFeedbackError }) {
            state.autoSettingUpFeedbackError = autoSettingUpFeedbackError;
        },
        setSyncingSigningKeys (state, { syncingSigningKeys }) {
            state.syncingSigningKeys = syncingSigningKeys;
        },
        setSyncingSigningKeysError (state, { syncingSigningKeysError }) {
            state.syncingSigningKeysError = syncingSigningKeysError;
        },
    },
    actions: {
        linkModels ({ commit }, { modelsToLink, vendorUuid } = {}) {
            commit('setLinking', { linking: true });
            if (!modelsToLink) {
                throw new Error('No modelsToLink Given');
            }

            if (!vendorUuid) {
                throw new Error('No vendorUuid Given');
            }

            return linkModelsInVendor({ modelsToLink, vendorUuid })
                .then(({ data: linkingResult }) => {
                    commit('setLinkingResult', { linkingResult });
                    commit('setLinking', {
                        linking: false,
                    });
                })
                .catch((linkingError) => {
                    commit('setLinking', {
                        linking: false,
                    });
                    commit('setLinkingError', { linkingError });
                    throw linkingError;
                });
        },
        unLinkModels ({ commit }, { modelsToUnLink } = {}) {
            commit('setUnLinking', { unLinking: true });
            if (!modelsToUnLink) {
                throw new Error('No modelsToUnLink Given');
            }

            return unLinkModelsInVendor({ modelsToUnLink })
                .then(({ data: unLinkingResult }) => {
                    commit('setUnLinkingResult', { unLinkingResult });
                    commit('setUnLinking', {
                        unLinking: false,
                    });
                })
                .catch((unLinkingError) => {
                    commit('setUnLinking', {
                        unLinking: false,
                    });
                    commit('setUnLinkingError', { unLinkingError });
                    throw unLinkingError;
                });
        },
        autoCreateVendor ({ commit }) {
            commit('setCreatingVendor', { creatingVendor: true });

            return autoCreateVendor()
                .then(() => {
                    commit('setCreatingVendor', {
                        creatingVendor: false,
                    });
                })
                .catch((creatingVendorError) => {
                    commit('setCreatingVendor', {
                        creatingVendor: false,
                    });
                    commit('setCreatingVendorError', { creatingVendorError });
                    throw creatingVendorError;
                });
        },
        migrateGroups ({ commit }, { vendorUuid } = {}) {
            commit('setMigratingGroups', { migratingGroups: true });

            if (!vendorUuid) {
                throw new Error('No vendorUuid Given');
            }

            return migrateGroupsToVendor({ vendorUuid })
                .then(({ data: migratingGroupsResult }) => {
                    commit('setMigratingGroups', {
                        migratingGroups: false,
                    });
                    commit('setMigratingGroupsResult', {
                        migratingGroupsResult,
                    });
                })
                .catch((migratingGroupsError) => {
                    commit('setMigratingGroups', {
                        migratingGroups: false,
                    });
                    commit('setMigratingGroupsError', { migratingGroupsError });
                    throw migratingGroupsError;
                });
        },
        autoSetupFeedback ({ commit }, { applicationIds, subscription } = {}) {
            if (!applicationIds) {
                throw new Error('No applicationIds Given');
            }

            if (!subscription) {
                throw new Error('No subscription Given');
            }
            commit('setAutoSettingUpFeedback', { autoSettingUpFeedback: true });

            return autoSetupFeedback({
                applicationIds,
                subscriptionName: subscription.name,
            })
                .then(() => {
                    commit('setAutoSettingUpFeedback', {
                        autoSettingUpFeedback: false,
                    });
                })
                .catch((error) => {
                    commit('setAutoSettingUpFeedback', {
                        autoSettingUpFeedback: false,
                    });
                    commit('setAutoSettingUpFeedbackError', {
                        autoSettingUpFeedbackError: error.response.data,
                    });
                    throw error;
                });
        },
        async syncSigningKeys ({ commit }, { vendorUuid } = {}) {
            commit('setSyncingSigningKeys', { syncingSigningKeys: true });
            commit('setSyncingSigningKeysError', { syncingSigningKeysError: null });

            if (!vendorUuid) {
                throw new Error('No vendorUuid Given');
            }

            try {
                await syncSigningKeys({ vendorUuid });
            } catch (error) {
                commit('setSyncingSigningKeysError', {
                    syncingSigningKeysError: error,
                });
            }
            commit('setSyncingSigningKeys', { syncingSigningKeys: false });
        },
    },
};
